import { createFileRoute, useNavigate, useRouterState, Link, Outlet } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import logoAsset from "@/assets/logo.png.asset.json";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { LanguageSwitcher, useT } from "@/lib/i18n";


export const Route = createFileRoute("/auth")({
  ssr: false,
  component: AuthPage,
  head: () => ({
    meta: [
      { title: "Přihlášení — Cerveza.International" },
      { name: "description", content: "Přihlaste se nebo se zaregistrujte do Cerveza.International." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

// Only allow same-origin relative paths as post-auth destinations. Anything
// else (absolute URLs, protocol-relative, backslashes) is dropped so a
// malicious link can't bounce a signed-in user to an off-site page.
function sanitizeNext(raw: string | undefined | null): string | undefined {
  if (!raw || typeof raw !== "string") return undefined;
  if (!raw.startsWith("/")) return undefined;
  if (raw.startsWith("//") || raw.startsWith("/\\")) return undefined;
  return raw;
}

async function resolveDestination(userId: string, fallback?: string): Promise<string> {
  const { data } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId);
  const roles = (data ?? []).map((r) => r.role);
  // Explicit `next` (e.g. from OAuth consent) always wins so the user lands back
  // on the exact URL that sent them through auth.
  if (fallback && fallback !== "/auth" && fallback !== "/admin") return fallback;
  // Vakant owners always land in their section
  if (roles.includes("vakant_owner") && !roles.includes("admin") && !roles.includes("moderator")) {
    return "/majitele-vacantu";
  }
  if (roles.includes("admin") || roles.includes("vacanty_admin")) return "/admin";
  return "/moje-podniky";
}

function AuthPage() {
  const navigate = useNavigate();
  const { t } = useT();
  const searchObj = useRouterState({ select: (s) => s.location.search }) as unknown as Record<string, string>;
  // Accept both `next` (OAuth consent flow) and legacy `redirect`.
  const requestedRedirect = sanitizeNext(searchObj?.next) ?? sanitizeNext(searchObj?.redirect);
  // Child routes (e.g. /auth/reset-password) render instead of the sign-in
  // form. Critical: a recovery link creates a session, so the auto-redirect
  // below MUST NOT run for child routes — it would bounce the user away
  // before they can set a new password.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isChildRoute = pathname !== "/auth";


  const forgotParam = searchObj?.forgot === "1" || searchObj?.forgot === "true";
  const [tab, setTab] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [forgotOpen, setForgotOpen] = useState<boolean>(!!forgotParam);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSubmitting, setForgotSubmitting] = useState(false);

  // If the URL requests the "forgot password" dialog, keep it in sync when it
  // arrives after mount (e.g. client-side navigation from /auth/reset-password).
  useEffect(() => {
    if (forgotParam) setForgotOpen(true);
  }, [forgotParam]);

  const sendResetEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotSubmitting(true);
    const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
      redirectTo: window.location.origin + "/auth/reset-password",
    });
    setForgotSubmitting(false);
    if (error) return toast.error(error.message);
    toast.success("Pokud e-mail existuje, odeslali jsme odkaz pro obnovu hesla.");
    setForgotOpen(false);
    setForgotEmail("");
  };


  const resendConfirmation = async () => {
    if (!email) return toast.error(t("auth.enterEmailFirst"));
    setResending(true);
    try {
      const { resendConfirmationEmail } = await import("@/lib/auth-resend.functions");
      const res = await resendConfirmationEmail({
        data: { email, redirectTo: window.location.origin + "/moje-podniky" },
      });
      toast.success(t("auth.resendResult", { remaining: res.remaining }));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("auth.resendFailed"));
    } finally {
      setResending(false);
    }
  };

  useEffect(() => {
    // When the user explicitly requested the "forgot password" flow, don't
    // auto-redirect them away — they may already be signed in and simply want
    // to send themselves another reset link. Child routes (reset password with
    // a recovery session) must never be redirected either.
    if (forgotParam || isChildRoute) return;
    let redirected = false;
    const go = async (userId: string) => {
      if (redirected) return;
      redirected = true;
      // Attach referral if a ?ref=CODE was captured before sign-up
      try {
        const { attachStoredReferral } = await import("@/lib/referral-attach");
        await attachStoredReferral();
      } catch { /* ignore */ }
      const dest = await resolveDestination(userId, requestedRedirect);
      navigate({ to: dest, replace: true });
    };
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) void go(data.session.user.id);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      if ((event === "SIGNED_IN" || event === "INITIAL_SESSION") && s?.user) {
        void go(s.user.id);
      }
    });
    return () => sub.subscription.unsubscribe();
  }, [navigate, requestedRedirect, forgotParam, isChildRoute]);

  const signIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    setSubmitting(false);
    if (error) return toast.error(error.message);
    toast.success(t("auth.welcomeBack"));
    const dest = await resolveDestination(data.user!.id, requestedRedirect);
    navigate({ to: dest });
  };

  const signUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    // Preserve `next` (e.g. OAuth consent URL) through the confirmation email
    // round-trip. Falls back to /moje-podniky when there's no explicit target.
    // Carry the referral code through the confirmation round-trip so the link
    // still attributes correctly when the e-mail is opened on another device.
    let refSuffix = "";
    try {
      const { getStoredReferralCode } = await import("@/lib/referral-tracker");
      const stored = getStoredReferralCode();
      if (stored) refSuffix = `${(requestedRedirect ?? "/moje-podniky").includes("?") ? "&" : "?"}ref=${stored}`;
    } catch { /* ignore */ }
    const emailRedirectTo =
      window.location.origin + (requestedRedirect ?? "/moje-podniky") + refSuffix;
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo,
        data: { full_name: name },
      },
    });
    setSubmitting(false);
    if (error) return toast.error(error.message);

    // Supabase returns a user with an EMPTY identities array when the email
    // is already registered (anti-enumeration). Treat that as "already exists"
    // and tell the user to confirm or sign in — never create a duplicate.
    const identities = (data.user as { identities?: unknown[] } | null)?.identities;
    if (data.user && Array.isArray(identities) && identities.length === 0) {
      toast.message(t("auth.emailAlreadyRegistered"));
      // Fire-and-forget: notify the real owner of the address that someone
      // tried to re-register, and include a password-reset link.
      try {
        const { notifyDuplicateSignup } = await import("@/lib/duplicate-signup-notify.functions");
        void notifyDuplicateSignup({ data: { email, siteOrigin: window.location.origin } });
      } catch { /* ignore */ }
      setTab("signin");
      return;
    }


    if (data.session && data.user) {
      const dest = await resolveDestination(data.user.id, requestedRedirect);
      navigate({ to: dest });
      return;
    }

    // No session = email confirmation required
    navigate({ to: "/", search: { pendingVerification: true } });
    return;
  };

  const oauth = async (provider: "google" | "apple") => {
    // Round-trip `next` through /auth so the consent flow (or any other
    // caller) lands the user on the exact URL they came from.
    const returnTo = requestedRedirect
      ? `/auth?next=${encodeURIComponent(requestedRedirect)}`
      : "/auth";
    const result = await lovable.auth.signInWithOAuth(provider, {
      redirect_uri: window.location.origin + returnTo,
    });
    if (result.error) toast.error(result.error.message ?? t("auth.oauthFailed"));
  };


  if (isChildRoute) return <Outlet />;

  return (
    <div className="grid min-h-screen place-items-center bg-background p-4">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2">
            <img
              src={logoAsset.url}
              alt="Cerveza.International"
              className="h-10 w-10 rounded-lg object-cover"
            />
            <span className="font-serif text-xl">Cerveza.International</span>
          </Link>
          <LanguageSwitcher />
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-2xl">
              {tab === "signin" ? t("auth.signIn") : t("auth.signUp")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={tab} onValueChange={(v) => setTab(v as "signin" | "signup")}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="signin">{t("auth.tabSignIn")}</TabsTrigger>
                <TabsTrigger value="signup">{t("auth.tabSignUp")}</TabsTrigger>
              </TabsList>

              <div className="mt-5 grid gap-2">
                <Button type="button" variant="outline" onClick={() => oauth("google")}>
                  {t("auth.google")}
                </Button>
                <Button type="button" variant="outline" onClick={() => oauth("apple")}>
                  {t("auth.apple")}
                </Button>
              </div>

              <div className="my-5 flex items-center gap-3">
                <Separator className="flex-1" />
                <span className="text-xs text-muted-foreground">{t("auth.or")}</span>
                <Separator className="flex-1" />
              </div>

              <TabsContent value="signin" className="mt-0">
                <form onSubmit={signIn} className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="email">{t("auth.email")}</Label>
                    <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="password">{t("auth.password")}</Label>
                    <div className="relative">
                      <Input id="password" type={showPassword ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} className="pr-10" />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
                        title={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
                        className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" className="w-full" disabled={submitting}>
                    {submitting ? t("auth.submitSignInLoading") : t("auth.submitSignIn")}
                  </Button>
                  <div className="flex items-center justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => { setForgotEmail(email); setForgotOpen(true); }}
                      className="text-xs text-muted-foreground underline-offset-2 hover:underline"
                    >
                      Zapomenuté heslo?
                    </button>
                    <button
                      type="button"
                      onClick={resendConfirmation}
                      disabled={resending}
                      className="text-xs text-muted-foreground underline-offset-2 hover:underline disabled:opacity-50"
                    >
                      {resending ? t("auth.resending") : t("auth.resendConfirmation")}
                    </button>
                  </div>
                </form>
              </TabsContent>


              <TabsContent value="signup" className="mt-0">
                <form onSubmit={signUp} className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="name">{t("auth.name")}</Label>
                    <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="email-up">{t("auth.email")}</Label>
                    <Input id="email-up" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="password-up">{t("auth.passwordMin")}</Label>
                    <div className="relative">
                      <Input id="password-up" type={showPassword ? "text" : "password"} minLength={6} required value={password} onChange={(e) => setPassword(e.target.value)} className="pr-10" />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
                        title={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
                        className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" className="w-full" disabled={submitting}>
                    {submitting ? t("auth.submitSignUpLoading") : t("auth.submitSignUp")}
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    {t("auth.firstAdminNote")}
                  </p>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Obnovit heslo</DialogTitle>
              <DialogDescription>
                Zadejte e-mail spojený s vaším účtem. Pošleme vám odkaz pro nastavení nového hesla.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={sendResetEmail} className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="forgot-email">E-mail</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  required
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                />
              </div>
              <DialogFooter>
                <Button type="submit" className="w-full" disabled={forgotSubmitting}>
                  {forgotSubmitting ? "Odesílám…" : "Odeslat odkaz"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>


        <p className="mt-4 text-center text-xs text-muted-foreground">
          <Link to="/" className="hover:underline">{t("auth.back")}</Link>
        </p>
      </div>
    </div>
  );
}
